import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';

/**
 * Styles for the triple-panel support desk. Mirrors the Claude Design "Support Inbox" prototype,
 * mapped onto `THEME` tokens so the desk inherits the product palette/spacing/typography rather than
 * hardcoded hex. Details is the default right-panel tab; the collapsed views rail lives in the list
 * header (back-arrow + switcher).
 */

// The prototype's `--lv-shadow-lg` — used for the larger Search criteria panel. Not a palette
// token, so it's a literal here rather than forced through THEME.colors.
const SHADOW_LG = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';

// Lifts the peeked ticket list off the thread it covers. A literal for the same reason as SHADOW_LG.
const PEEK_SHADOW = '6px 0 20px -8px rgb(0 0 0 / 0.28)';

/** Desk list column: full width, and the rail it collapses to while a thread is open. */
export const TICKET_LIST_WIDTH_PX = 300;
export const TICKET_LIST_RAIL_WIDTH_PX = 48;

// Row hover wash — the prototype uses a faint cool gray; background.main is the closest token.
const ROW_HOVER = THEME.colors.background.main;

export const inboxStyles = StyleSheet.create({
	// ---- Root grid ----
	desk: {
		height: '100%',
		overflow: 'hidden',
		minHeight: 0,
		// The support surface is all Sora (per Sean, diverging from the prototype's Lexend body
		// copy); components-v2 controls keep their own font defaults.
		fontFamily: THEME.typography.fontFamilies.sora,
		backgroundColor: THEME.colors.background.main,
	},
	phoneConversationStage: {
		height: '100%',
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
	},
	deskColumn: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 0,
	},
	deskBody: {
		flex: 1,
		minHeight: 0,
	},
	notAgentNotice: {
		flex: 'none',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		borderBottom: `1px solid ${THEME.colors.warning.main}`,
		backgroundColor: THEME.colors.warning.light,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	mergedTicketNotice: {
		flex: 'none',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		borderBottom: `1px solid ${THEME.colors.info.main}`,
		backgroundColor: THEME.colors.info.light,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	paneGrid: {
		display: 'grid',
		height: '100%',
		minHeight: 0,
		overflow: 'hidden',
	},
	viewItemLabel: {
		flex: 1,
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	viewCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},

	// ---- Pane 2: ticket list ----
	listPane: {
		minWidth: 0,
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
		borderRight: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.light,
		// Anchors the phone new-ticket button.
		position: 'relative',
	},

	// ---- Collapsed ticket list: rail + hover peek, while a thread is open and the list is unpinned ----
	// Only the rail takes grid width; the pane floats out of it, so peeking never reflows the thread.
	// The stack clears the thread beside it and stays under every popover layer (30-60, including the
	// menus this pane's own view switcher and Selects portal to the body).
	listRegion: {
		position: 'relative',
		minWidth: 0,
		minHeight: 0,
		zIndex: 20,
	},
	// The hover zone, and the handle the pane slides out from behind — so it stays clickable while
	// the pane is open.
	listRail: {
		position: 'relative',
		zIndex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '100%',
		height: '100%',
		paddingTop: 12,
		paddingBottom: 12,
		borderRight: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.light,
	},
	listPaneFloating: {
		position: 'absolute',
		top: 0,
		bottom: 0,
		left: TICKET_LIST_RAIL_WIDTH_PX,
		width: TICKET_LIST_WIDTH_PX,
		boxShadow: PEEK_SHADOW,
		transition: 'transform 180ms ease',
		'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
	},
	listPaneTucked: {
		transform: 'translateX(-100%)',
		// Keeps the off-screen list out of the tab order and the accessibility tree; the hide waits
		// for the slide to finish.
		visibility: 'hidden',
		transition: 'transform 180ms ease, visibility 0s linear 180ms',
		'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
	},

	// ---- Search bar + Search criteria popover, expanded from the list-header search icon ----
	searchBar: {
		flex: 'none',
		position: 'relative',
		zIndex: 30,
		padding: '10px 12px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	searchRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	searchInputWrap: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		flex: 1,
		minWidth: 0,
	},
	searchIcon: {
		position: 'absolute',
		left: 10,
		color: THEME.colors.text.light,
		pointerEvents: 'none',
	},
	searchInput: {
		width: '100%',
		height: 34,
		paddingLeft: 32,
		fontSize: THEME.typography.fontSizes.sm,
		// Safari zooms the page when a focused field is under 16px.
		'@media (max-width: 767px)': { fontSize: 16, height: 44 },
	},
	searchInputClearable: {
		paddingRight: 30,
	},
	searchClearBtn: {
		position: 'absolute',
		right: 4,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 26,
		height: 26,
		padding: 0,
		color: THEME.colors.text.light,
		':hover': { color: THEME.colors.text.main },
	},
	filterToggle: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 34,
		height: 34,
		flex: 'none',
		padding: 0,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		color: THEME.colors.text.light,
		cursor: 'pointer',
		':has(*) > svg': { width: 17, height: 17 },
		':hover': { borderColor: THEME.colors.hover.main, color: THEME.colors.text.main },
	},
	filterToggleActive: {
		borderColor: THEME.colors.primary.main,
		color: THEME.colors.primary.main,
		backgroundColor: THEME.colors.primary.light,
	},
	filterBadge: {
		position: 'absolute',
		top: -6,
		right: -6,
		minWidth: 16,
		height: 16,
		padding: '0 3px',
		boxSizing: 'border-box',
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.primary.main,
		border: `2px solid ${THEME.colors.background.light}`,
		color: THEME.colors.primary.contrastText,
		fontSize: 9,
		fontWeight: 700,
		lineHeight: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	filterOverlay: {
		position: 'fixed',
		inset: 0,
		zIndex: 40,
	},
	filterPanel: {
		position: 'absolute',
		left: 12,
		right: 12,
		top: 'calc(100% - 8px)',
		zIndex: 50,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: 12,
		boxShadow: SHADOW_LG,
		overflow: 'hidden',
		'@media (max-width: 767px)': {
			position: 'fixed',
			left: 0,
			right: 0,
			top: 'auto',
			bottom: 0,
			maxHeight: 'min(80dvh, 640px)',
			borderRadius: `${THEME.common.borderRadius}px ${THEME.common.borderRadius}px 0 0`,
		},
	},
	filterPanelHeader: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: '12px 14px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	filterPanelTitle: {
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	filterPanelClose: {
		marginLeft: 'auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 24,
		height: 24,
		padding: 0,
		border: 'none',
		borderRadius: 6,
		background: 'transparent',
		color: THEME.colors.text.light,
		cursor: 'pointer',
		':has(*) > svg': { width: 15, height: 15 },
		':hover': { backgroundColor: ROW_HOVER, color: THEME.colors.text.main },
	},
	filterPanelBody: {
		maxHeight: '56vh',
		overflowY: 'auto',
		padding: '12px 14px',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	filterRow: {
		display: 'flex',
		flexDirection: 'column',
	},
	filterRowTrigger: {
		width: '100%',
		height: 36,
		justifyContent: 'flex-start',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
		'@media (max-width: 767px)': { height: 44 },
	},
	filterRowLabel: {
		color: THEME.colors.text.light,
	},
	filterRowValue: {
		marginLeft: 'auto',
		color: THEME.colors.text.light,
	},
	filterRowValueActive: {
		color: THEME.colors.text.main,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	filterCsmLabel: {
		marginBottom: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	filterCsmInput: {
		height: 36,
		fontSize: THEME.typography.fontSizes.sm,
	},
	filterCheckRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		marginTop: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		cursor: 'pointer',
	},
	filterPanelFooter: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: '12px 14px',
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	filterClearBtn: {
		height: 'auto',
		padding: 0,
		border: 'none',
		background: 'transparent',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		cursor: 'pointer',
		':hover': { color: THEME.colors.text.main },
	},
	filterDoneBtn: {
		marginLeft: 'auto',
		height: 'auto',
		padding: '7px 18px',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	listHeader: {
		padding: '14px 12px 10px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		flex: 'none',
	},
	listHeaderRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 10,
		position: 'relative',
		minWidth: 0,
	},
	listHeaderLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		minWidth: 0,
		flex: '1 1 auto',
	},
	backBtn: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 24,
		height: 28,
		marginLeft: 0,
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		cursor: 'pointer',
		color: THEME.colors.text.main,
		flex: 'none',
		':has(*) > svg': { width: 'auto', height: 'auto' },
		':hover': { backgroundColor: ROW_HOVER },
	},
	listTitleBtn: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: 0,
		height: 'auto',
		background: 'transparent',
		border: 'none',
		padding: 0,
		cursor: 'pointer',
		minWidth: 0,
	},
	listTitleGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: 5,
		minWidth: 0,
		flex: '0 1 auto',
		overflow: 'hidden',
	},
	listTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		whiteSpace: 'nowrap',
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	listSubCount: {
		fontSize: 13,
		color: THEME.colors.text.light,
		flex: 'none',
		whiteSpace: 'nowrap',
	},
	// Phone keeps the count on the title row; a row of its own costs 24px of list.
	listInlineCount: {
		fontSize: 13,
		color: THEME.colors.text.light,
		flex: 'none',
		whiteSpace: 'nowrap',
	},
	// "New ticket" lives here rather than in the phone masthead: the masthead had six controls
	// fighting for 390px, and this sits in thumb reach.
	newTicketFab: {
		position: 'absolute',
		right: THEME.spacing.md,
		bottom: 20,
		zIndex: 5,
		width: 56,
		height: 56,
		padding: 0,
		borderRadius: 28,
		boxShadow: '0 6px 18px rgba(0, 66, 165, 0.32)',
	},
	newTicketFabAbovePagination: {
		bottom: 80,
	},
	listHeaderIconBtn: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 28,
		height: 28,
		padding: 0,
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		cursor: 'pointer',
		color: THEME.colors.text.light,
		flex: 'none',
		':has(*) > svg': { width: 16, height: 16 },
		':hover': { backgroundColor: ROW_HOVER, color: THEME.colors.text.main },
	},
	listHeaderIconBtnActive: {
		backgroundColor: THEME.colors.primary.light,
		color: THEME.colors.primary.main,
	},
	selectModeBtn: {
		height: 44,
		padding: `0 ${THEME.spacing.sm}px`,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	selectModeBtnActive: {
		backgroundColor: THEME.colors.primary.light,
		color: THEME.colors.primary.main,
	},
	phoneSelectionBar: {
		position: 'sticky',
		bottom: 0,
		zIndex: 4,
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		borderTop: `1px solid ${THEME.colors.border.main}`,
		backgroundColor: THEME.colors.primary.light,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	selectionError: {
		padding: THEME.spacing.sm,
		color: THEME.colors.danger.dark,
		fontSize: THEME.typography.fontSizes.xs,
	},
	switcherChevron: {
		flex: 'none',
		alignSelf: 'center',
		color: THEME.colors.text.main,
	},
	// Quick-jump view menu, rendered through the components-v2 Dropdown (base-ui Menu handles
	// positioning/dismissal), so this only sets the popup width.
	viewMenu: {
		width: 240,
	},
	viewMenuItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: 10,
		height: 'auto',
		padding: '8px 10px',
		border: 'none',
		borderRadius: 7,
		background: 'transparent',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		textAlign: 'left',
		':hover': { backgroundColor: ROW_HOVER },
		'@media (max-width: 767px)': { minHeight: 44, padding: `10px ${THEME.spacing.md}px` },
	},
	viewMenuItemActive: {
		backgroundColor: THEME.colors.primary.light,
	},
	listScroll: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
		// Clearance for the floating new-ticket button, so it never covers the last row.
		'@media (max-width: 767px)': { paddingBottom: 84 },
	},
	paginationBar: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: THEME.spacing.xs,
		padding: '9px 10px',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.light,
	},
	paginationPages: {
		display: 'flex',
		alignItems: 'center',
		gap: 3,
		minWidth: 0,
		overflow: 'hidden',
	},
	paginationArrow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 28,
		height: 28,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		color: THEME.colors.text.main,
		cursor: 'pointer',
		flex: 'none',
		':has(*) > svg': { width: 16, height: 16 },
		':hover': { backgroundColor: ROW_HOVER },
		':disabled': {
			cursor: 'default',
			color: THEME.colors.icon.disabled,
			backgroundColor: 'transparent',
		},
	},
	paginationPage: {
		minWidth: 28,
		height: 28,
		padding: '0 7px',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		color: THEME.colors.text.light,
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		':hover': {
			backgroundColor: ROW_HOVER,
			color: THEME.colors.text.main,
		},
		':disabled': {
			cursor: 'default',
		},
	},
	paginationPageActive: {
		backgroundColor: THEME.colors.primary.light,
		color: THEME.colors.primary.main,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	phonePaginationBar: {
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	phonePaginationButton: {
		minWidth: 88,
		height: 44,
	},
	phonePaginationCount: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		whiteSpace: 'nowrap',
	},
	ticketRow: {
		display: 'flex',
		alignItems: 'stretch',
		justifyContent: 'flex-start',
		flexDirection: 'column',
		gap: 6,
		width: '100%',
		height: 'auto',
		padding: '10px 16px',
		fontFamily: THEME.typography.fontFamilies.sora,
		borderRadius: 0,
		cursor: 'pointer',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		borderLeft: '3px solid transparent',
		textAlign: 'left',
		':hover': { backgroundColor: ROW_HOVER },
		'@media (max-width: 767px)': { minHeight: 72, padding: `${THEME.spacing.md}px` },
	},
	ticketRowActive: {
		backgroundColor: THEME.colors.primary.light,
		borderLeftColor: THEME.colors.primary.main,
		':hover': { backgroundColor: THEME.colors.primary.light },
	},
	// Pending tickets wait on the customer and Snoozed ones on a clock — sunk to the bottom of the page and dimmed.
	ticketRowDimmed: {
		opacity: 0.6,
	},
	ticketActiveText: {
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	ticketDimmedText: {
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
	},
	rowTop: {
		display: 'flex',
		alignItems: 'center',
		gap: 9,
	},
	rowAvatar: {
		width: 30,
		height: 30,
		flex: 'none',
	},
	rowName: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
		flex: 1,
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowTime: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		flex: 'none',
	},
	rowSubject: {
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowPreview: {
		fontSize: 13,
		color: THEME.colors.text.light,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowPills: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		flexWrap: 'wrap',
	},

	// ---- Pane 3: conversation ----
	conversationPane: {
		position: 'relative',
		minWidth: 0,
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.main,
		overflow: 'hidden',
		// Desktop stretches this as a grid item; the phone stage is a flex column, where the pane has to
		// claim the space left by the context header and field strip.
		'@media (max-width: 767px)': { flex: 1 },
	},

	// ---- Pane 4: drawer (Assistant / Details) ----
	supportDrawer: {
		minWidth: 0,
		minHeight: 0,
		height: '100%',
		overflow: 'hidden',
		'@media (max-width: 1279px)': {
			position: 'fixed',
			top: 0,
			right: 0,
			bottom: 0,
			zIndex: 50,
			pointerEvents: 'none',
			'> *': { pointerEvents: 'auto' },
		},
	},
	supportDrawerContent: {
		borderLeft: `1px solid ${THEME.colors.border.light}`,
		overflowY: 'hidden' as const,
		'@media (max-width: 1279px)': {
			position: 'relative',
			height: '100dvh',
			maxWidth: '100%',
			width: 'min(456px, 100vw)',
			pointerEvents: 'auto',
			boxShadow: '0 0 0 100vmax rgba(0, 0, 0, 0.3)',
		},
	},
	supportDrawerPlaceholder: {
		width: 456,
		minWidth: 456,
		minHeight: 0,
		display: 'flex',
		borderLeft: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.light,
	},
	rightContent: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		padding: 16,
	},
	rightAssistantContent: {
		flex: 1,
		minHeight: 0,
		overflow: 'hidden',
	},
	detailHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 11,
		paddingBottom: 14,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	detailAvatar: {
		width: 42,
		height: 42,
		flex: 'none',
	},
	detailHealthScore: {
		marginLeft: 'auto',
	},
	// The contact block shares the row with the avatar and health score, so it has to give way and
	// wrap rather than push a long address past the panel edge.
	detailIdentity: {
		minWidth: 0,
	},
	detailName: {
		fontSize: 15,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	detailEmail: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.primary.main,
		overflowWrap: 'anywhere',
	},
	detailCustomerAction: {
		height: 'auto',
		justifyContent: 'flex-start',
		padding: 0,
		fontFamily: 'inherit',
		fontSize: THEME.typography.fontSizes.xs,
	},
	detailSectionTitle: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
		margin: '16px 0 10px',
	},
	detailRows: {
		display: 'flex',
		flexDirection: 'column',
		gap: 9,
	},
	// Created / Messages sit below the editable controls; add separation from them.
	detailRowsStacked: {
		marginTop: 12,
	},
	// Editable Status / Priority / Assignee / Category controls (relocated from the app bar).
	detailEditRows: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	detailEditRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
	},
	detailRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 12,
	},
	detailKey: {
		fontSize: 13,
		color: THEME.colors.text.light,
	},
	detailValue: {
		fontSize: 13,
		color: THEME.colors.text.main,
		textAlign: 'right',
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	detailCompany: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: THEME.spacing.sm,
		maxWidth: '100%',
	},

	// ---- Advertising (ads module mirror) ----
	// Carries the section's vertical rhythm, which detailSectionTitle supplies for the other headings.
	adsToggleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		margin: '16px 0 10px',
	},
	adsToggle: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		flex: 1,
		minWidth: 0,
		padding: 0,
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		fontFamily: 'inherit',
		textAlign: 'left',
	},
	adsToggleChevron: {
		flex: 'none',
		color: THEME.colors.text.light,
		transition: 'transform 150ms ease-out',
	},
	adsToggleChevronCollapsed: {
		transform: 'rotate(-90deg)',
	},
	adsToggleTitle: {
		marginRight: 'auto',
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
	},
	adsToggleSummary: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	adsCards: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		paddingBottom: THEME.spacing.xs,
	},
	adsCard: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: 10,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 9,
	},
	adsRows: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	// Chips wrap under the channel name rather than squeezing it: at drawer width a stage like
	// "Onboarding (no show)" plus a live pill does not fit on the title's line.
	adsCardHeader: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: THEME.spacing.xs,
	},
	adsCardTitle: {
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		marginRight: 'auto',
	},
	adsBlockLabel: {
		margin: 0,
	},
	adsNote: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		padding: THEME.spacing.sm,
		borderRadius: 9,
		backgroundColor: THEME.colors.background.main,
	},
	adsNoteByline: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	// Notes run long and the drawer is the wrong place to read a full one; the account details page
	// holds the whole note plus its composer.
	adsNoteBody: {
		// @ts-expect-error - aphrodite's typings have no -webkit-box
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 3,
		overflow: 'hidden',
		fontSize: 13,
		color: THEME.colors.text.main,
		overflowWrap: 'anywhere',
		whiteSpace: 'pre-wrap',
	},
	adsHealthPills: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.xs,
	},
	adsHealthError: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		padding: THEME.spacing.sm,
		borderRadius: 9,
		border: `1px solid ${THEME.colors.danger.main}`,
		backgroundColor: THEME.colors.danger.light,
	},
	adsHealthErrorLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.dark,
	},
	adsHealthErrorMessage: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontFamily: 'monospace',
		color: THEME.colors.danger.dark,
		overflowWrap: 'anywhere',
	},
	detailAccountStatus: {
		fontWeight: THEME.typography.fontWeights.bold,
	},
	detailAccountStatus_active: {
		color: THEME.colors.success.dark,
	},
	detailAccountStatus_cancelled: {
		color: THEME.colors.danger.main,
	},
	detailAccountStatus_pending: {
		color: THEME.colors.warning.dark,
	},
	detailAccountStatus_neutral: {
		color: THEME.colors.text.main,
	},
	detailLinks: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
	},
	detailLink: {
		fontSize: 13,
		color: THEME.colors.primary.main,
		textDecoration: 'none',
		':hover': { textDecoration: 'underline' },
	},
	detailInlineLink: {
		height: 'auto',
		padding: 0,
		fontFamily: 'inherit',
		fontSize: 13,
	},
	detailLinkSeparator: {
		color: THEME.colors.text.light,
	},

	// ---- Previous conversations (other tickets from the same contact) ----
	linkedList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 7,
	},
	linkedRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: 9,
		width: '100%',
		height: 'auto',
		minHeight: 0,
		textAlign: 'left',
		padding: '9px 11px',
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 9,
		background: 'transparent',
		cursor: 'pointer',
		color: THEME.colors.text.main,
		fontFamily: THEME.typography.fontFamilies.sora,
		':hover': { backgroundColor: THEME.colors.hover.light },
	},
	linkedIcon: {
		flex: 'none',
		color: THEME.colors.text.light,
		display: 'flex',
	},
	linkedText: {
		flex: 1,
		minWidth: 0,
		fontSize: 13,
		color: THEME.colors.text.main,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	linkedViewAll: {
		color: THEME.colors.primary.main,
		fontWeight: THEME.typography.fontWeights.semibold,
	},

	// ---- Shared empty / placeholder states ----
	empty: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 10,
		padding: 32,
		textAlign: 'center',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	loading: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 32,
	},

	// ---- Reports screen (page shell shared with Reports/SupportReportsScreen) ----
	reportsScreen: {
		height: '100%',
		minHeight: 0,
		overflowY: 'auto',
		backgroundColor: THEME.colors.background.main,
		padding: '24px 28px',
	},
	reportsInner: {
		width: '100%',
		maxWidth: 1320,
		margin: '0 auto',
		display: 'flex',
		flexDirection: 'column',
		gap: 18,
	},
	reportsTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
		color: THEME.colors.text.main,
	},
});
